<template>
	<v-alert
		v-if="!dismissed"
		class="incomplete-upload-banner"
		type="warning"
		variant="tonal"
		density="compact"
		rounded="lg"
		:closable="dismissible"
		close-label="Hide this reminder"
		v-test="'session-incomplete-upload-banner'"
		@click:close="dismissConversation(conversationId)">
		<div class="d-flex flex-column flex-sm-row align-sm-center ga-2">
			<div class="incomplete-upload-banner__message flex-grow-1">
				<!-- LSP-17986: both permutations now name WHERE the recording is stuck before offering the remedy.
				     "Didn't finish uploading" alone reads as a server-side failure the admin can only wait out; the
				     missing chunks are actually sitting in IndexedDB on the capture device, which is the one fact
				     that makes the button worth pressing.

				     The apostrophe is U+2019, not ASCII. This PR made the opening sentence identical to the one
				     MediaLightBox.incompleteUploadMessage() renders for viewers who cannot act, and that one has
				     always used the typographic glyph — so leaving this file on ASCII meant the same sentence
				     rendered two different ways depending on which surface you hit it from. -->
				<!-- LSP-18264: the banner now also fires when only photos/files are unfinished (the manual Send
				     Reminder was extended to cover them), so the copy must not always say "recording"/"video".
				     `pendingKind` selects the media-accurate wording; it defaults to "recording", keeping every
				     existing recording mount and its copy tests unchanged. -->
				<template v-if="pendingKind === 'recording'">
					<span v-if="hasCustomer">
						This recording didn’t finish uploading from the device it was taken on. Do you want to send your customer a link to
						finish uploading the video?
					</span>
					<span v-else>
						This recording didn’t finish uploading from the device it was taken on. Do you want to send a link to the device to
						finish uploading the video?
					</span>
				</template>
				<template v-else>
					<span v-if="hasCustomer">
						Some files didn’t finish uploading from the device they were added on. Do you want to send your customer a link to
						finish uploading them?
					</span>
					<span v-else>
						Some files didn’t finish uploading from the device they were added on. Do you want to send a link to the device to
						finish uploading them?
					</span>
				</template>
			</div>
			<v-btn
				class="flex-shrink-0"
				variant="flat"
				color="warning"
				size="small"
				rounded="xl"
				:loading="sending"
				v-test="'send-upload-reminder'"
				@click="sendReminder">
				{{ buttonLabel }}
			</v-btn>
		</div>
	</v-alert>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, ref, watch } from "vue";
	import { useToast } from "vue-toastification";
	import api from "../api";
	import { logError, logWarn } from "@/helpers/logError";
	import {
		cooldownExpiryFor,
		dismiss as dismissConversation,
		isDismissed,
		hasReminderBeenSent,
		markReminderSent,
		clearInFlight,
		isInFlight,
		isOnCooldown as conversationIsOnCooldown,
		markInFlight,
		recordCooldown,
		reminderStateVersion
	} from "@/helpers/incompleteUploadReminderState";

	// LSP-17328. Two things were wrong with the old inline banner:
	//
	// 1. It only rendered inside MediaLightBox's `v-if="isDesktop"` metadata block, BELOW the video.
	//    Under 960px — a halved window, a portrait monitor — it disappeared entirely, so the recording
	//    looked complete when it wasn't. It now renders above the video at every width.
	// 2. Knowing the upload is incomplete didn't help you fix it. The missing chunks are in IndexedDB
	//    on the device that did the recording, which is almost never the desktop you're reviewing
	//    from, and browser eviction can take them at any time. The fastest resolution is getting that
	//    person back onto that device, so the banner now carries the nudge itself.
	const props = withDefaults(
		defineProps<{
			conversationId: string;
			/** A customer-recorded session reopens their own link; a self-recorded one is the user's own device. */
			hasCustomer: boolean;
			/**
			 * LSP-18264: which unfinished media the banner is speaking about. "recording" keeps the original
			 * recording/video wording (the default, so every existing mount and its copy tests are unchanged);
			 * "media" is used when only photos/files are still uploading, so the copy stops claiming a recording.
			 */
			pendingKind?: "recording" | "media";
			/**
			 * cursor (LSP-16985 follow-up): whether this banner may hide itself.
			 *
			 * On the SESSION page, `true` is right: the banner speaks for the whole session, so an explicit
			 * "I have dealt with this" close should clear it. In the LIGHTBOX it is wrong, and dangerously so
			 * — dismissal is shared module state, so a close on the session page would blank the warning over
			 * a recording the admin then opens. An incomplete recording looking complete is the exact defect
			 * this feature exists to prevent, so the per-recording instance is never allowed to vanish.
			 *
			 * LSP-17965: a successful SEND no longer dismisses anything, on either mount. Only the admin's own
			 * close does, because the upload is still unfinished after a reminder goes out.
			 */
			dismissible?: boolean;
		}>(),
		{ dismissible: true, pendingKind: "recording" }
	);

	// Mirrors MANUAL_UPLOAD_REMINDER_COOLDOWN_SECONDS on the server; only ever used when the response
	// doesn't carry the authoritative value.
	const DEFAULT_COOLDOWN_SECONDS = 300;
	// Client-side ceiling on any server-supplied window. The server's own window is env-configurable, so
	// this is deliberately generous — it honors a cooldown raised well above the default — while keeping
	// a nonsense value from disabling the button for longer than a coffee break.
	// One hour, and deliberately NOT the server's 24h ceiling, which review pointed out this no longer
	// matches. The two failure modes are not equally bad. Clamping BELOW the server's window re-enables the
	// button early and costs one cheap 429; clamping AT the server's ceiling lets a hostile or mistaken
	// `retryAfter` — a units mix-up sending milliseconds, a rewriting proxy — disable it for a day, which is
	// the dead button this feature exists to end and is only recoverable by a reload. An hour is far above
	// any plausible reminder cooldown, so the mismatch only bites if someone configures the server past it,
	// which is a config-review problem rather than a runtime one.
	const MAX_COOLDOWN_SECONDS = 60 * 60;
	// Longest server `message` we will render into a toast. A non-JSON error response (a proxy's HTML
	// error page, say) arrives with the whole document in `message`, and a client-side JSON parse
	// failure arrives with a SyntaxError's text — neither is copy to show an admin.
	const MAX_SERVER_MESSAGE_LENGTH = 300;

	// ls-heroku-ci #5: one string, two guards (the client-side window below and the server's 429 path).
	// They must say the same thing — they describe the same situation to the same person.
	const ALREADY_SENT_MESSAGE = "A reminder was sent recently. Please wait before sending another.";

	const toast = useToast();
	// WHICH conversations have a request in flight — a set, not a boolean and not a single id.
	// A boolean was cleared by the conversation watcher on the way out, so an A -> B -> A carousel trip
	// could not tell that A's original request was still running and issued a second concurrent send
	// for A (break-it round 6). A single id has the same hole once B is also in flight. The guard is
	// per-conversation: B may send while A is unresolved (round 3), A may not send twice (round 6).
	// Seeded from the module so a remount mid-request does not present an idle button over a live request.
	const sending = computed(() => {
		void reminderStateVersion();
		return isInFlight(props.conversationId);
	});

	// The authoritative client-side deadline. ls-heroku-ci: this used to continue "nothing paints it any
	// more — the banner simply disappears on a successful send", which stopped being true anywhere once
	// LSP-17965 removed that dismissal: NEITHER mount vanishes on a send now, and `alreadySent` paints
	// "Reminder sent" from this deadline on both. Corrected here as well as at startCooldown, because a
	// maintainer who believes the banner always vanishes concludes `markReminderSent`/`alreadySent` are
	// redundant and removes the confused-re-click guard. This deadline is not painted directly by anything
	// after a successful send — but it still short-circuits a click from a banner that has come back (a
	// reload, or a revisit) while the server's window is open, so that click cannot become a second real
	// message. It is stored as an absolute deadline rather than a remaining count, because a background tab
	// throttles timers and a decrement-per-tick value drifts arbitrarily far behind (round 5).
	// Per-conversation, like the in-flight set. A single deadline reset on every conversation change,
	// so an A -> B -> A carousel trip showed "Send Reminder" as available while the server's Redis
	// window for A still stood — the client only re-learned the truth from the resulting 429.
	// Dismissal is per-mount and deliberately NOT persisted (no localStorage, no server flag): the upload
	// is still unfinished after the admin closes this, so a permanent dismissal would hide a live problem
	// for good. Closing it clears the banner for now; it returns on the next visit or reload, which is what
	// "not permanent" has to mean for a warning about data that is still at risk. LSP-17965 made the "next
	// visit" half of that true rather than aspirational — module state outlives an in-SPA navigation, so
	// EntityDetailPage now calls `clearDismissal` for the conversation it navigates TO.
	// Seeded FROM the module, so a remount across the smAndDown breakpoint does not resurrect a banner the
	// admin already closed. Only an EXPLICIT close gets here now: LSP-17965 removed the dismissal a
	// successful send used to record, which was permanently hiding a warning the send did not resolve.
	// A computed over the shared state rather than a ref mirroring it. The ref was seeded at setup and
	// re-read only when `conversationId` changed, so a dismissal made by ANOTHER instance of the same
	// conversation never reached this one. Reading `reminderStateVersion()` makes every instance repaint
	// together, and drops the conversationId watcher entirely — the computed already re-evaluates when the
	// prop changes, which is what that watcher was hand-rolling.
	const dismissed = computed(() => {
		void reminderStateVersion();
		return props.dismissible && isDismissed(props.conversationId);
	});

	/**
	 * cursor: bumped when a cooldown LAPSES, which no other signal reports.
	 *
	 * I wrote the warning about this in incompleteUploadReminderState's docstring and then built exactly
	 * what it warns against: `stateVersion` makes the set of conversations observable, never the passage of
	 * time, so a label derived from `isOnCooldown` went stale at the one moment it mattered — it kept
	 * reading "Reminder sent" after the window had genuinely expired, while a click right then does issue a
	 * real second reminder. A label that lies precisely when the button starts working again is worse than
	 * no label.
	 *
	 * One timeout at the deadline, NOT the per-second countdown that lsp17328 deliberately removed. That
	 * countdown was removed for answering a question nobody asked; this fires once, to stop saying something
	 * untrue.
	 */
	const cooldownLapsedTick = ref(0);
	let cooldownExpiryTimer: ReturnType<typeof setTimeout> | undefined;

	function armCooldownExpiry(): void {
		if (cooldownExpiryTimer !== undefined) {
			clearTimeout(cooldownExpiryTimer);
			cooldownExpiryTimer = undefined;
		}
		const remaining = cooldownExpiryFor(props.conversationId) - Date.now();
		if (remaining <= 0) return;
		cooldownExpiryTimer = setTimeout(() => {
			cooldownExpiryTimer = undefined;
			cooldownLapsedTick.value += 1;
		}, remaining);
	}

	/**
	 * Whether a reminder went out for this conversation AND the server's window is still open.
	 *
	 * ls-heroku-ci raised this as a duplicate-send defect and proposed blocking any second send for the
	 * page load. I have deliberately NOT done that: lsp17329 round 5 pins the opposite decision — once the
	 * server's authoritative window lapses, a second send is allowed, because the upload is still
	 * unfinished (that is why the banner is still here) and re-nudging a customer who did not act is the
	 * feature, not a bug. A page-load-scoped block would also be untrue to the server, which would accept
	 * the request.
	 *
	 * What the finding got right is legibility: as of LSP-17965 the banner no longer vanishes on a send on
	 * either mount, so a button still reading "Send Reminder" invites an immediate confused re-click. That
	 * click is already refused by the cooldown guard; this makes the refusal predictable instead of
	 * surprising. Scoped to the window so the offer visibly returns when it genuinely does.
	 */
	const alreadySent = computed(() => {
		void reminderStateVersion();
		void cooldownLapsedTick.value;
		return hasReminderBeenSent(props.conversationId) && conversationIsOnCooldown(props.conversationId);
	});

	// Re-armed whenever a cooldown is recorded (the version bump) or this instance is handed a different
	// recording — each conversation carries its own deadline.
	watch([() => props.conversationId, reminderStateVersion], armCooldownExpiry, { immediate: true });

	// A send can still be in flight when the lightbox closes or the carousel moves on. Without this,
	// the response's startCooldown() would write state AFTER onBeforeUnmount already ran, and
	// nothing would ever clear it (break-it round 1).
	let isMounted = true;

	const buttonLabel = computed(() => {
		if (sending.value) return "Sending…";
		// Past tense once a reminder has actually gone out. The repo's convention keeps action buttons
		// enabled and no-ops the handler, so the LABEL is what has to explain why a click does nothing —
		// and a button that still reads "Send Reminder" after a successful send invites exactly the second
		// send this guards against. NOT shown for a mere cooldown: there the offer is genuinely still
		// coming back, which lsp17328 round 9 settled.
		if (alreadySent.value) return "Reminder sent";
		return "Send Reminder";
	});

	// The server owns the real window; this only mirrors it. Anything that isn't a positive finite
	// number — 0, null, "soon", a missing field — falls back to the default rather than silently
	// leaving the button live, which would let a spam-click straight through to the API.
	// Ceil, not round: a 0.49s window rounded to zero and left the button immediately live again
	// (break-it round 3). Any positive window costs at least one second.
	function coerceCooldown(seconds: unknown): number {
		return firstValidCooldown(seconds);
	}

	// `api._handleResponse` rejects with the parsed JSON BODY itself — `{ code, message, errorCode?,
	// retryAfter? }`, with `code` defaulted to the HTTP status — not with an axios-style
	// `{ response: { status, data } }`. Reading the axios shape meant every 429 fell through to the
	// generic branch in production: no cooldown started, the rate-limit copy never appeared, and each
	// retry hit the API again until Redis refused it. The axios path stays as a fallback in case a
	// caller ever wraps this, but the body is the real shape.
	//
	// Response HEADERS are deliberately not consulted: this client discards them on the error path, so
	// reading a Retry-After header here would be permanently dead code.
	function errorPayload(error: any): Record<string, unknown> {
		return (error?.response?.data ?? error ?? {}) as Record<string, unknown>;
	}

	function errorStatus(error: any): number {
		const payload = errorPayload(error);
		return Number(payload.code ?? payload.status ?? error?.response?.status);
	}

	// The server's message is rendered straight into a toast, so only a real, non-empty string is
	// allowed through — an object or an array would reach the renderer as "[object Object]"
	// (break-it round 3).
	//
	// Round 9 added the rest of the shape check. `_handleResponse` puts `await r.text()` into `message`
	// when a response is not JSON, so a proxy's HTML error page reached `toast.error()` as a ~2KB
	// document; and when `r.json()` itself throws, `errorPayload` falls back to the Error, whose
	// `.message` ("Unexpected end of JSON input") was shown to the admin as if the server had said it.
	// Two conditions rule both out: it has to come from something that actually looks like an API body
	// (a `code`/`status` is present), and it has to be short enough and free of markup to be prose.
	function serverMessage(error: any): string | null {
		const payload = errorPayload(error);
		const looksLikeApiBody = payload.code !== undefined || payload.status !== undefined;
		const message = payload.message;
		if (!looksLikeApiBody || typeof message !== "string") return null;
		const trimmed = message.trim();
		if (!trimmed || trimmed.length > MAX_SERVER_MESSAGE_LENGTH || trimmed.startsWith("<")) return null;
		return trimmed;
	}

	// Body first, then header, then the default — but only a source that actually parses to a positive
	// number counts. Taking the body whenever it is merely present let a malformed value shadow a
	// valid header and fabricate a full-length wait (break-it round 5).
	//
	// Clamped and type-checked as of break-it round 9. The server clamps its own Retry-After now, but
	// this is the side that pays for a bad one: nothing here bounded the value, so `retryAfter: 1e12`
	// (a units mix-up sending milliseconds, a rewriting proxy, a rolling deploy) disabled the button
	// until a page reload. A ceiling costs nothing and removes the whole class. `typeof` first, because
	// `Number(true) === 1` sailed through the finite-and-positive guard and recorded a one-second
	// window — the guard's own comment claimed non-numeric values fall back to the default, and a
	// boolean was quietly proving it wrong. Numeric strings stay welcome; the server sends a number,
	// but a proxy that re-serializes may not.
	function firstValidCooldown(...candidates: unknown[]): number {
		for (const candidate of candidates) {
			if (typeof candidate !== "number" && typeof candidate !== "string") continue;
			const parsed = Number(candidate);
			if (Number.isFinite(parsed) && parsed > 0) return Math.min(Math.ceil(parsed), MAX_COOLDOWN_SECONDS);
		}
		return DEFAULT_COOLDOWN_SECONDS;
	}

	function isOnCooldown(): boolean {
		return conversationIsOnCooldown(props.conversationId);
	}

	/**
	 * Record the window this send earned, against the conversation it was sent FOR.
	 *
	 * The owner argument matters: a send that resolves after the carousel has moved on still earned a window
	 * for its own conversation, and dropping it meant returning there showed the button as available until
	 * the next click learned the truth from a 429.
	 *
	 * cursor: this used to add "nothing is painted from this — the banner disappears on a successful send",
	 * which stopped being true in this same change. LSP-17965 removed that disappearance from BOTH mounts,
	 * not just the lightbox's `dismissible: false` one — `alreadySent` now paints "Reminder sent" from the
	 * recorded window everywhere. What the deadline still does beyond painting is guard a click from a
	 * banner that has come back (a reload, or a revisit) while the server's window is open.
	 */
	function startCooldown(seconds: number, owner: string = props.conversationId): void {
		const total = Math.max(0, Math.ceil(seconds));
		if (total === 0) return;
		recordCooldown(owner, Date.now() + total * 1000);
		// ls-heroku-ci: the trailing `if (!isMounted || owner !== props.conversationId) return;` is GONE. It
		// guarded a per-second countdown that no longer exists, so it had nothing after it in either branch —
		// dead code whose comment described behaviour the function had stopped implementing. Every instance
		// now reads the recorded deadline through `alreadySent`, which is keyed on the conversation, so there
		// is nothing left here that needs to care whether this instance is the one on screen.
	}

	async function sendReminder(): Promise<void> {
		// Guarded in the handler rather than via :disabled, per the repo's form-button convention —
		// and because a disabled button mid-countdown reads as broken rather than as "already sent".
		// This is only a courtesy guard: the authoritative cooldown is the server's Redis window, since
		// the client is trivially bypassed.
		// A plain return, deliberately — and the toast that briefly lived here has been removed.
		//
		// cursor: `:loading="sending"` compiles to `.v-btn--loading { pointer-events: none }`, so a real
		// click cannot reach this line at all while a send is in flight; the toast was unreachable outside
		// happy-dom, where `trigger("click")` bypasses CSS. It was also unnecessary: `sending` is shared
		// per-conversation, so EVERY mounted instance shows the spinner (and Vuetify hides the label behind
		// it). The button visibly reports itself. This stays as a programmatic backstop only.
		// LSP-17965: both arms of this gate log which condition fired, per the repo's silent-failure
		// diagnostic rule — not just the one that caused the last incident. A refused click is otherwise
		// indistinguishable from a broken button in a bug report, and reconstructing "was it the latch or the
		// window?" from indirect signals is exactly the archaeology the rule exists to end. Routed through
		// `logWarn` (not a bare `console.warn`) so this diagnostic also reaches Datadog, matching every other
		// silent-failure log site in this app.
		if (sending.value) {
			logWarn("[incomplete-upload] send reminder ignored: a request is already in flight", {
				conversationId: props.conversationId
			});
			return;
		}
		// cursor (LSP-16985 follow-up): the cooldown guard used to `return` in silence. That was invisible on
		// the session page, where a successful send made the banner disappear — there was no button left to
		// click. The lightbox mounts one banner per carousel item and never hides them, so a send from one
		// recording leaves its siblings showing a live button for a conversation already on cooldown, and a
		// click there hit this silent return: the dead button this component spent rounds eliminating.
		// Says the same thing the server's 429 path says, keeping "enabled button, reason in a toast"
		// (lsp17328 round 9) true for the client-side window too.
		//
		// LSP-17965 made this the ONLY thing standing between a click and a second real message on the
		// session page too, since a successful send no longer hides the banner. The remaining window is
		// logged with it: "the admin says the button does nothing" is only triageable if the logs say how
		// much longer it will do nothing for.
		if (isOnCooldown()) {
			logWarn("[incomplete-upload] send reminder ignored: still inside the recorded cooldown window", {
				conversationId: props.conversationId,
				remainingMs: cooldownExpiryFor(props.conversationId) - Date.now()
			});
			// break-it round 2 (LSP-17965) found this call unguarded too — same failure mode as the
			// 429/generic-failure branches below: a torn-down toast library throwing here escapes
			// `sendReminder` as an unhandled rejection, since this gate sits before the function's own
			// try/catch entirely.
			try {
				toast.info(ALREADY_SENT_MESSAGE);
			} catch (notifyError: unknown) {
				// bot review: logError, not logWarn — a raw Error nested inside logWarn's `context` object
				// JSON-serializes to `{}` (Error.message/.stack are non-enumerable), silently dropping the
				// detail from Datadog. logError's dedicated `error` param exists for exactly this shape.
				logError("IncompleteUploadBanner: could not surface the cooldown notice", notifyError, {
					conversationId: props.conversationId
				});
			}
			return;
		}

		// Pinned for the whole call. This instance can be handed a different recording while the
		// request is in flight, and a cooldown or a "sent" toast belongs to the conversation it was
		// earned on — applying either to whatever is on screen now would both lie to the admin and
		// block a reminder for a session that never got one (break-it round 2).
		const requestedConversationId = props.conversationId;

		markInFlight(requestedConversationId);
		// ls-heroku-ci: whether the SERVER already accepted the send, as distinct from whether everything
		// after it went smoothly. The try below wraps the API call and all the post-success work, so a throw
		// in the latter — a toast library on a torn-down container, say — landed in the catch and told the
		// admin "We couldn't send the reminder" about a message that had already gone out, while skipping the
		// cooldown and sent bookkeeping. The obvious response to that message is to press the button again,
		// which delivered a real SECOND SMS and email to the customer. Misreporting a success is bad;
		// turning the misreport into a duplicate customer message is the part that matters.
		let serverAcceptedSend = false;
		// cursor (LSP-16985 follow-up): the duplicate-prevention bookkeeping the catch may still OWE, as opposed
		// to bookkeeping it should redo. Non-null only between "the server confirmed a channel delivered" and
		// "both writes landed", and it carries the SERVER's window.
		//
		// The recovery branch below used to re-record DEFAULT_COOLDOWN_SECONDS unconditionally, which broke in
		// the very failure it was written for: the success toast runs LAST, so by the time a throw from it
		// arrives, `result.cooldownSeconds` has already been recorded. `recordCooldown` stores an ABSOLUTE
		// deadline rather than extending one, so the default silently REPLACED the server's value — cutting a
		// longer window short and re-arming a button that could then only 429, or holding a shorter one dead for
		// minutes longer than the server asked. Resuming what is unfinished is idempotent; redoing it is not.
		let unrecordedBookkeeping: { cooldownSeconds: number } | null = null;
		// Hoisted so the recovery branch can tell a CONFIRMED send from the "2xx naming no channel" case below,
		// which deliberately records nothing and must not be talked into a success message by a throw.
		let deliveredChannels = "";
		try {
			const result = await api.sendUploadReminder(requestedConversationId);
			serverAcceptedSend = true;
			// The lightbox can close, or the carousel move on, while the request is in flight. Nothing
			// after this point belongs to a component that no longer exists — announcing "reminder sent"
			// over whatever the user is looking at now is worse than saying nothing (break-it round 1).
			// The send itself already happened server-side, which is what actually matters.
			const stillOnScreen = isMounted && requestedConversationId === props.conversationId;
			// Strict === true: a stringly-typed "false" from a proxy or a schema drift is truthy, and would
			// have had us tell the rep a text went out that never did (break-it round 1).
			deliveredChannels = [result?.smsSent === true ? "text" : null, result?.emailSent === true ? "email" : null]
				.filter(Boolean)
				.join(" and ");
			if (!deliveredChannels) {
				// A 2xx from this endpoint always names at least one channel it delivered on. An empty
				// body, or a shape we don't recognise, means we genuinely don't know whether anything
				// went out — so don't tell the customer's rep that it did, and don't burn the window on a
				// send we cannot vouch for.
				// bot review: logError (not logWarn) — this is a genuine anomaly (an unrecognized response
				// shape from our own API), the same severity the bare console.error it replaces already
				// claimed; logError forwards it to Datadog with structured context instead of losing the
				// conversationId that made it, along with the response body, un-correlatable and console-only.
				logError(
					"IncompleteUploadBanner: upload reminder returned an unexpected response shape",
					new Error("Unrecognized upload reminder response shape"),
					{ conversationId: requestedConversationId, result }
				);
				if (stillOnScreen) {
					toast.error("We couldn't confirm the reminder was sent. Please check with the customer before resending.");
				}
				return;
			}
			// Window recorded against the conversation it was earned on, whether or not that is what the
			// admin is now looking at.
			// The banner STAYS. LSP-17965: it used to dismiss itself here, which took the warning and its
			// button away permanently for the rest of the SPA session — the reported bug. Sending a reminder
			// does not finish the upload, so everything the warning says is still true one second after the
			// send: the chunks are still stranded on the recording device, and browser eviction can still
			// take them. A warning about data that is still at risk must outlive the action that was taken
			// about it, which is precisely how the 429 branch below already behaves.
			// Still no countdown, though — a disabled button labelled "Sent (287s)" answers a question nobody
			// asked and reads like a malfunction. The second-send guard is the recorded window plus the
			// server's own Redis window, never the absence of a banner or a greyed-out button; the button
			// stays enabled and its click is refused with an explanation, per the repo's form-button
			// convention.
			// Captured BEFORE the write, so a throw from `startCooldown` itself leaves the catch holding the
			// server's number rather than falling back to the default.
			const confirmedCooldownSeconds = coerceCooldown(result?.cooldownSeconds);
			unrecordedBookkeeping = { cooldownSeconds: confirmedCooldownSeconds };
			startCooldown(confirmedCooldownSeconds, requestedConversationId);
			// UNCONDITIONAL. Gating this on `stillOnScreen` meant a remount mid-request — an orientation
			// change, a window drag across the breakpoint — left the send unrecorded, because the instance
			// that learned about the success was already dying. The send happened; that fact belongs to the
			// conversation, not to whichever instance happened to be on screen when the response landed.
			// Only the TOAST is gated below, which is right — a toast is for the person still looking.
			markReminderSent(requestedConversationId);
			// Both writes landed, so the catch owes nothing further. Everything after this point is display.
			unrecordedBookkeeping = null;
			if (!stillOnScreen) return;
			toast.success(`Reminder sent by ${deliveredChannels}.`);
		} catch (error: any) {
			const stillOnScreen = isMounted && requestedConversationId === props.conversationId;
			// ls-heroku-ci: the send SUCCEEDED and something after it threw. Never report that as a failed
			// send, and above all do not leave the duplicate-prevention bookkeeping unrecorded — that is what
			// turns a cosmetic fault into a second real message.
			if (serverAcceptedSend) {
				// cursor: FINISH the bookkeeping, never redo it. `unrecordedBookkeeping` is already null when the
				// try got through both writes (the common case — the throw came from the toast that runs after
				// them), and re-recording there would overwrite the server's absolute deadline with the default.
				// It is also null on the "2xx naming no channel" path, which decided on purpose to record
				// nothing; a throw from its toast must not quietly reverse that.
				if (unrecordedBookkeeping) {
					startCooldown(unrecordedBookkeeping.cooldownSeconds, requestedConversationId);
					markReminderSent(requestedConversationId);
				}
				// break-it round (bot review): routed through logError, not a bare console.warn — the
				// dedicated `error` param is what actually gets `error.message`/`.stack` to Datadog; a raw
				// Error nested inside a `context` object JSON-serializes to `{}` (non-enumerable properties),
				// which is exactly what the `logWarn` sibling catches below were fixed to avoid.
				logError(
					"IncompleteUploadBanner: the reminder was sent but post-send processing failed; recording the send so it cannot be repeated",
					error,
					{ conversationId: requestedConversationId }
				);
				if (stillOnScreen) {
					// Wrapped because we are HERE, in most cases, precisely because a toast threw — and the same
					// broken toast layer will throw again. Unguarded, that second throw escaped `sendReminder`
					// as an unhandled rejection, after all the bookkeeping had already landed. There is no
					// remedy left to attempt at this point and nothing further to protect, so the last cosmetic
					// step must not be able to take the handler down with it (break-it round 3).
					try {
						// Only vouch for what the server named a channel for — same bar as the try's own toasts.
						if (deliveredChannels) toast.success("Reminder sent.");
						else toast.error("We couldn't confirm the reminder was sent. Please check with the customer before resending.");
					} catch (notifyError: unknown) {
						// break-it round 2 (LSP-17965): routed through logError, not a bare console.warn — this
						// file's own convention (see the sibling catches around it) is that a swallowed toast
						// failure must still reach Datadog, correlated by conversationId, or it is invisible to
						// the exact triage the diagnostic-logging rule exists for. logError's dedicated `error`
						// param (not a raw Error nested inside a context object, which JSON-serializes to `{}`)
						// is what actually gets `.message`/`.stack` into the payload (bot review).
						logError("IncompleteUploadBanner: could not surface the post-send notice", notifyError, {
							conversationId: requestedConversationId
						});
					}
				}
				return;
			}
			// Number(), not ===: a stringly-typed "429" would otherwise fall through to the generic
			// branch, skip the cooldown entirely, and leave the button free to hammer the API (round 2).
			// bot review: corrected — there is no header fallback. Only the JSON body's `retryAfter` field is
			// ever read; `errorPayload()` deliberately never consults response headers (see its own docstring
			// above), so a proxy that strips or rewrites the body costs us the server's real window outright,
			// with the default as the only fallback (round 4).
			const status = errorStatus(error);
			const candidateRetryAfter = errorPayload(error).retryAfter;
			const parsedRetryAfter = Number(candidateRetryAfter);
			const serverRetryAfter = Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0 ? candidateRetryAfter : undefined;
			// Adopt a server window only on a 429. The "claimed but delivered nothing" case this branch was
			// written for IS a 429 (the server throws RateLimitError there and keeps the cooldown), so
			// nothing is lost by scoping it — while honoring a window on ANY status meant a refusal that
			// merely happened to carry the field would disable the button for minutes even though nothing
			// was sent and nothing was claimed. Safe only by omission today, since 422s don't include it:
			// one schema drift and a "still uploading from the mobile app" refusal locks the button
			// (break-it round 9).
			if (status === 429) {
				// Recorded UNCONDITIONALLY, and outside the `stillOnScreen` branch below. The server is holding a
				// window whether or not the admin is still looking at this conversation, so gating the record on
				// that was the same instance-scoped mistake as the send dismissal: navigate away during the
				// request and the next visit offered a button that could only 429 again. A 429 with no
				// `retryAfter` still means a window exists, so it falls back to the default rather than nothing.
				startCooldown(
					serverRetryAfter !== undefined ? firstValidCooldown(serverRetryAfter) : DEFAULT_COOLDOWN_SECONDS,
					requestedConversationId
				);
			} else {
				// ls-heroku-ci review (medium): the underlying `error` was reaching the admin as a toast and
				// NOWHERE else — every sibling branch of this catch forwards its failure to Datadog, but the
				// one branch that represents an actual unexplained send failure logged nothing, so "the button
				// says it couldn't send" was unanswerable without reproducing it. `logWarn`, not `logError`:
				// this is a server refusal or a transient network fault the admin can simply retry, not the
				// invariant violation the `logError` siblings above report. `status` is included because it is
				// the field that separates a 422 refusal from a 500 or an offline browser (0/undefined).
				//
				// bot review (Cursor Bugbot): emitted UNCONDITIONALLY, ABOVE the `stillOnScreen` gate — the
				// same instance-scoped correction the 429 cooldown record above already carries. This log first
				// landed below the gate, so the single most triage-relevant case (the admin clicks Send and
				// navigates away or moves the carousel while the request is in flight) dropped its Datadog
				// signal entirely and left exactly the blind spot this branch was added to close. A failure
				// belongs to the CONVERSATION and is logged either way; only the TOAST below is gated, which is
				// right — a toast is for the person still looking at this banner. Only plain values are passed,
				// nothing read from a component that may already be torn down.
				logWarn("[incomplete-upload] send reminder failed", {
					conversationId: requestedConversationId,
					status: status ?? null,
					serverMessage: serverMessage(error),
					errorMessage: error instanceof Error ? error.message : String(error)
				});
			}
			if (!stillOnScreen) return;
			// break-it round 2 (LSP-17965): wrapped, matching the post-send recovery branch above. The
			// bookkeeping this catch owes (the cooldown record on the 429 branch) already landed before this
			// point either way, so a torn-down toast library here has nothing left to protect — but unguarded
			// it escaped `sendReminder` as an unhandled rejection, which is the exact asymmetry that branch
			// was already fixed for.
			try {
				if (status === 429) {
					// Someone else — another admin, another tab, or the automatic job — already sent one.
					// Adopt the server's window instead of letting this tab keep retrying into 429s.
					toast.info(serverMessage(error) ?? ALREADY_SENT_MESSAGE);
				} else {
					// The diagnostic for this branch already fired above the `stillOnScreen` gate; what is left
					// here is purely what the admin sees.
					// Deliberately no cooldown on other failures: nothing was sent, so making the admin wait
					// five minutes to retry a transient error would just delay the upload we're chasing.
					toast.error(serverMessage(error) ?? "We couldn't send the reminder. Please try again.");
				}
			} catch (notifyError: unknown) {
				// bot review: logError, not logWarn — see the cooldown-notice catch above for why a raw Error
				// inside logWarn's context object silently loses its message/stack in Datadog.
				logError("IncompleteUploadBanner: could not surface the send-failure notice", notifyError, {
					conversationId: requestedConversationId
				});
			}
		} finally {
			// Only the request that still owns this component clears the latch. A late `finally` from a
			// request started for a previous conversation was releasing the CURRENT conversation's
			// in-flight latch, letting a second call go out while its first was still unresolved
			// (break-it round 4).
			clearInFlight(requestedConversationId);
		}
	}

	// The carousel can hand this same instance a different recording. A cooldown belongs to the
	// conversation it was earned on, so carrying it over would silently block a reminder for a
	// completely different session (break-it round 1).
	// ls-heroku-ci #4: the watcher that used to live here is GONE, not emptied. It had already been reduced
	// to an empty body — every value it once recomputed on a conversation change is now a computed keyed on
	// `props.conversationId`, which re-evaluates on its own — so what remained was a `watch` that ran and
	// did nothing, which reads to the next maintainer as a hook with a missing implementation.
	//
	// The reasoning it carried is worth keeping, because it documents a deliberate NON-action:
	// - The cooldown deadline for an arriving conversation is read from its STORED expiry rather than
	//   re-derived and re-applied, which would re-anchor (and so extend) the window on every visit.
	// - The in-flight set is deliberately NOT cleared on a conversation change. `sending` is derived
	//   per-conversation, so the button already reads as idle for a different session, and keeping the
	//   entry means a trip back still knows the original request is running (rounds 3 and 6).

	onBeforeUnmount(() => {
		if (cooldownExpiryTimer !== undefined) clearTimeout(cooldownExpiryTimer);
		isMounted = false;
	});
</script>

<style scoped>
	.incomplete-upload-banner {
		/* Above the video and full-width, but visually quiet — the ticket asks for more visible AND
		   less obtrusive, so this is a slim tonal strip rather than the old bordered error block. */
		margin-bottom: 0.75rem;
	}

	.incomplete-upload-banner__message {
		font-size: 0.875rem;
		line-height: 1.35;
	}
</style>
